import IMAGES from '@/assets/images';
import CustomImage from '@/components/CustomImage';
import { DatePicker, Empty, Flex, Skeleton, Space } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useState } from 'react';
import { useGetTenantUsersWithShiftAssignmentsQuery } from '../../../../../../app/features/employee/emp-attendance/emp-attendance.api';
import type { ShiftAssignParams } from '../../../../../../app/features/employee/emp-attendance/emp-attendance.type';
import {
  useGetMonthlyHolidaysQuery,
  useGetMonthlyLeavesQuery,
} from '../../../../../../app/features/employee/emp-leave/emp-leave.api';
import CustomPagination from '../../../../../../components/CustomPagination';
import SectionCard from '../../../../../../components/SectionCard';
import {
  DB_DATE_FORMAT,
  TIME_FORMAT,
} from '../../../../../../constants/global.constants';
import useAuth from '../../../../../../hooks/useAuth';
import useError from '../../../../../../hooks/useError';
import useFilters from '../../../../../../hooks/useFilters';
import { useQueryFilters } from '../../../../../../hooks/useQueryFilters';
import { holidayTypes } from '../../../_data/data';
import NextIcon from './_icons/NextIcon';
import PrevIcon from './_icons/PrevIcon';

export default function RosterCalendar() {
  const { tenantId, organizationId } = useAuth();
  const { filter, limit, page, handlePagination, resetPagination } =
    useFilters<Record<string, string | number>>();
  const { filters: queryFilter } = useQueryFilters<ShiftAssignParams>(
    {},
    resetPagination,
  );
  const [clicked, setClicked] = useState<'next' | 'prev' | undefined>(
    undefined,
  );
  const [dates, setDates] = useState(() =>
    Array.from({ length: 7 }, (_, i) => dayjs().add(1, 'day').add(i, 'day')),
  );

  const { error, isLoading, isFetching, data } =
    useGetTenantUsersWithShiftAssignmentsQuery({
      params: {
        ...filter,
        ...queryFilter,
        organizationId,
        tenantId,
        startDate: dates?.[0]?.format(DB_DATE_FORMAT),
        endDate: dates?.[6]?.format(DB_DATE_FORMAT),
      },
    });

  const { data: holidaysData, isLoading: holidaysLoading } =
    useGetMonthlyHolidaysQuery({
      params: {
        organizationId,
        startDate: dates?.[0]?.format(DB_DATE_FORMAT),
        endDate: dates?.[6]?.format(DB_DATE_FORMAT),
      },
    });

  const { data: leavesData, isLoading: leavesLoading } =
    useGetMonthlyLeavesQuery({
      params: {
        organization_id: organizationId,
        start_date: dates?.[0]?.format(DB_DATE_FORMAT),
        end_date: dates?.[6]?.format(DB_DATE_FORMAT),
      },
    });

  useError(error, 'Failed to fetch roster data');
  const loading = isLoading || isFetching || holidaysLoading || leavesLoading;

  const getHolidayLabel = (type: string) => {
    const holidayType = holidayTypes.find((ht) => ht.value === type);
    return holidayType ? holidayType.label : type;
  };

  // Create a map of holidays by date with priority
  const holidaysByDate = new Map();
  holidaysData?.data?.data?.forEach((holiday) => {
    const holidayDate = dayjs(holiday.date).format(DB_DATE_FORMAT);
    const holidayTypeIndex = holidayTypes.findIndex(
      (ht) => ht.value === holiday.type,
    );

    if (!holidaysByDate.has(holidayDate)) {
      holidaysByDate.set(holidayDate, {
        label: getHolidayLabel(holiday.type),
        priority: holidayTypeIndex !== -1 ? holidayTypeIndex : 999,
      });
    } else {
      // Keep the holiday with higher priority (lower index)
      const existing = holidaysByDate.get(holidayDate);
      if (holidayTypeIndex !== -1 && holidayTypeIndex < existing.priority) {
        holidaysByDate.set(holidayDate, {
          label: getHolidayLabel(holiday.type),
          priority: holidayTypeIndex,
        });
      }
    }
  });

  // Create a map of leaves by employee and date
  const leavesByEmployee = new Map();
  leavesData?.data?.data?.forEach((leave) => {
    if (leave.status === 'APPROVED') {
      const startDate = dayjs(leave.start_date);
      const endDate = dayjs(leave.end_date);
      const employeeId = leave.employee.user_id;

      if (!leavesByEmployee.has(employeeId)) {
        leavesByEmployee.set(employeeId, new Set());
      }

      // Add all dates in the leave range
      let currentDate = startDate;
      while (
        currentDate.isBefore(endDate) ||
        currentDate.isSame(endDate, 'day')
      ) {
        leavesByEmployee
          .get(employeeId)
          .add(currentDate.format(DB_DATE_FORMAT));
        currentDate = currentDate.add(1, 'day');
      }
    }
  });

  // Build a photo lookup map from the monthly leaves response (has employee.photo)
  const photoByEmployee = new Map<string, string | null>();
  leavesData?.data?.data?.forEach((leave) => {
    const { user_id, photo } = leave.employee;
    if (user_id && !photoByEmployee.has(user_id)) {
      photoByEmployee.set(user_id, photo ?? null);
    }
  });

  

  // Transform API data to match the expected structure
  const employees = data?.data?.data?.map((user) => {
    // Create a map of all dates with their assignments
    const dateMap = new Map();

    // Initialize all dates with empty state
    dates.forEach((date) => {
      const dateStr = date.format(DB_DATE_FORMAT);

      dateMap.set(dateStr, {
        date: dateStr,
        is_unassigned: 1,
        is_holiday: 0,
        is_shift: 0,
        is_leave: 0,
        is_general: 0,
        is_weekend: 0,
      });
    });

    // Step 1: Fill in actual shift assignments
    user.assignments?.forEach((assignment) => {
      const assignmentDate = dayjs(assignment.date);
      const dateStr = assignmentDate.format(DB_DATE_FORMAT);

      if (dateMap.has(dateStr)) {
        dateMap.set(dateStr, {
          id: assignment.id,
          date: dateStr,
          is_weekend: (assignment as any)?.is_weekend ? 1 : 0,
          is_holiday: 0,
          is_shift: assignment.is_general ? 0 : 1,
          is_leave: 0,
          is_unassigned: 0,
          is_general: assignment.is_general ? 1 : 0,
          shift_title: assignment.shift?.name,
          shift_name: assignment.shift?.name,
          shift_color: '#0F172A',
          shift_start: assignment.startTime,
          shift_end: assignment.endTime,
          additional_settings: {
            shift_id: assignment.shiftId,
          },
        });
      }
    });

    // Step 2: Add leaves for this employee (overrides shifts)
    const employeeLeaves = leavesByEmployee.get(user.user_id);
    if (employeeLeaves) {
      employeeLeaves.forEach((leaveDate: any) => {
        if (dateMap.has(leaveDate)) {
          const existing = dateMap.get(leaveDate);
          dateMap.set(leaveDate, {
            ...existing,
            is_leave: 1,
            is_shift: 0,
            is_general: 0,
            is_unassigned: 0,
          });
        }
      });
    }

    // Step 3: Add holidays (highest priority, overrides everything)
    dates.forEach((date) => {
      const dateStr = date.format(DB_DATE_FORMAT);
      const holiday = holidaysByDate.get(dateStr);
      const existing = dateMap.get(dateStr);

      // Check if this date should be marked as holiday
      if (holiday || existing?.is_weekend) {
        dateMap.set(dateStr, {
          ...existing,
          is_holiday: 1,
          is_leave: 0,
          is_shift: 0,
          is_general: 0,
          is_unassigned: 0,
          holiday_label: holiday?.label || 'Weekly Holiday',
        });
      }
    });

    const dateArray = Array.from(dateMap.values());

    return {
      employee_id: user.employee_id,
      name: user.name,
      department_name: user?.department?.name,
      designation_name: user?.department?.name,
      pro_pic: user.photo ?? null,
      shifts: dateArray?.map((shift) => ({
        type: shift?.is_holiday
          ? shift?.holiday_label
          : shift?.is_leave
            ? 'On Leave'
            : shift?.is_general
              ? 'General'
              : shift?.is_shift
                ? shift?.shift_title
                : 'Un Assigned',
        time:
          shift?.is_shift && shift?.shift_start && shift?.shift_end
            ? `${dayjs(shift?.shift_start, 'HH:mm').format(
                TIME_FORMAT,
              )} <br /> ${dayjs(shift?.shift_end, 'HH:mm').format(TIME_FORMAT)}`
            : undefined,
        date: shift?.date,
        shift_color: shift?.is_shift ? shift?.shift_color : undefined,
      })),
    };
  });

  const allowNext = employees
    ?.map((ep) => ep?.shifts)
    .some((array) => array?.length > 0);

  // Handle date range change
  const handleDateChange = (date: Dayjs) => {
    if (date) {
      const startFrom = date.add(1, 'day');
      const newDates = Array.from({ length: 7 }, (_, i) =>
        startFrom.add(i, 'day'),
      );
      setDates(newDates);
    }
  };

  // Navigate to the previous week
  const handlePrevWeek = () => {
    if (!allowNext && clicked === 'prev') return null;
    setClicked('prev');
    const startOfPrevWeek = dayjs(dates[0]).subtract(1, 'week');
    const newDates = Array.from({ length: 7 }, (_, i) =>
      startOfPrevWeek.add(i, 'day'),
    );
    setDates(newDates);
  };

  // Navigate to the next week
  const handleNextWeek = () => {
    if (!allowNext && clicked === 'next') return null;
    setClicked('next');
    const startOfNextWeek = dayjs(dates[0]).add(1, 'week');
    const newDates = Array.from({ length: 7 }, (_, i) =>
      startOfNextWeek.add(i, 'day'),
    );
    setDates(newDates);
  };

  return (
    <SectionCard className="max-w-full mt-6">
      <SectionCard.Title>
        <Flex align="center" justify="space-between" wrap>
          <h6>Roaster Calendar View</h6>
          <DatePicker
            onChange={handleDateChange}
            picker="week"
            value={dates[0]}
            className="max-w-[380px] relative roster_calendar_datepicker"
            open={false}
            allowClear={false}
            showToday={false}
            format={() => {
              return `${dayjs(dates[0]).format('MMMM DD')} - ${dayjs(
                dates[6],
              ).format('DD, YYYY')}` as unknown as string;
            }}
            suffixIcon={
              <div>
                <span
                  onClick={handlePrevWeek}
                  className={`absolute left-0 top-2/4 -translate-y-2/4 z-20 select-none pointer-events-auto ${
                    !allowNext && clicked === 'prev'
                      ? 'cursor-not-allowed text-gray-300'
                      : 'cursor-pointer text-theme-gray-700'
                  }`}
                >
                  <PrevIcon />
                </span>
                <span
                  onClick={handleNextWeek}
                  className={`absolute right-0 top-2/4 -translate-y-2/4 z-20 select-none pointer-events-auto ${
                    !allowNext && clicked === 'next'
                      ? 'cursor-not-allowed text-gray-300'
                      : 'cursor-pointer text-theme-gray-700'
                  }`}
                >
                  <NextIcon />
                </span>
              </div>
            }
          />
        </Flex>
      </SectionCard.Title>

      {/* SCROLL WRAPPER */}
      <div className="overflow-x-auto pb-2 custom-scroll">
        <SectionCard.Content className="max-h-max mt-8 space-y-0 min-w-[1200px]">
          <div className="flex items-center gap-2 sticky top-0 z-10">
            <Flex
              justify="center"
              align="center"
              className="w-[350px] min-h-28 px-4 py-2.5 dark:bg-theme-gray-700 dark:text-white text-xl font-bold rounded-xl"
            >
              Employee Info
            </Flex>
            <div className="flex-1 grid grid-cols-7 gap-2">
              {dates?.map((date, idx) => (
                <Flex
                  align="center"
                  justify="center"
                  key={idx}
                  className="min-h-28 p-3 dark:bg-theme-gray-700 dark:text-white rounded-xl text-center"
                >
                  <div>
                    <small className="text-lg font-medium">
                      {date.format('dddd')}
                    </small>

                    <h6 className="text-xl font-bold">
                      {date.format('MMM DD')}
                    </h6>
                  </div>
                </Flex>
              ))}
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="space-y-2 pt-2">
              {Array.from({ length: 5 }).map((_, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <Skeleton.Input
                    active
                    className="!w-[350px] !h-28 !rounded-xl"
                  />
                  <div className="flex-1 grid grid-cols-7 gap-2">
                    {Array.from({ length: 7 }).map((_, dateIdx) => (
                      <Skeleton.Button
                        key={dateIdx}
                        active
                        className="!h-28 !rounded-xl !w-full block"
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Employee Rows */}
          {!loading &&
            employees?.map((employee, idx) => {
              return (
                <div key={idx} className="flex gap-2 items-center text-sm pt-2">
                  <div className="w-[350px] h-28 px-4 py-2.5 dark:bg-theme-slate-900 rounded-xl">
                    <Space align="center" className="h-full" size={16}>
                      <CustomImage
                        width={80}
                        height={80}
                        alt={employee?.name}
                        className="rounded-full object-cover"
                        src={employee?.pro_pic || IMAGES.avatar}
                      />
                      <div className="dark:text-white text-base">
                        <h5 className="text-lg font-bold mb-2">
                          {employee.name}
                        </h5>
                        <p>{employee.designation_name || 'N/A'}</p>
                        <p className="font-bold">
                          {employee.employee_id ? employee.employee_id : 'N/A'}
                        </p>
                      </div>
                    </Space>
                  </div>
                  <div className="flex-1 grid grid-cols-7 gap-2 h-full">
                    {dates?.map((date, idx) => {
                      const matchingShift = employee?.shifts?.find(
                        (shift) =>
                          dayjs(shift?.date)?.format(DB_DATE_FORMAT) ===
                          date?.format(DB_DATE_FORMAT),
                      );

                      const type = matchingShift?.type;

                      return (
                        <Flex
                          align="center"
                          justify="center"
                          key={idx}
                          className={`h-28 p-3 border text-base font-medium dark:border-theme-gray-700 dark:text-white rounded-xl relative`}
                          style={{
                            backgroundColor:
                              matchingShift?.shift_color ?? 'transparent',
                            ...(matchingShift?.shift_color
                              ? {
                                  border: `1px solid ${matchingShift?.shift_color}`,
                                }
                              : {}),
                          }}
                        >
                          <div className="text-center">
                            <h6 className="dark:text-white">{type}</h6>
                            {matchingShift?.time && (
                              <span
                                className="dark:text-white font-bold block mt-1.5 text-base"
                                dangerouslySetInnerHTML={{
                                  __html: matchingShift?.time,
                                }}
                              />
                            )}
                          </div>
                        </Flex>
                      );
                    })}
                  </div>
                </div>
              );
            })}

          {employees?.length ? (
            <CustomPagination
              className="px-0 pt-6"
              total={data?.data?.total || 0}
              limit={limit}
              page={page}
              handlePagination={handlePagination}
            />
          ) : null}

          {/* Empty State */}
          {!isLoading && (!employees || employees.length === 0) && (
            <div className="text-center py-12">
              <Empty />
            </div>
          )}
        </SectionCard.Content>
      </div>
    </SectionCard>
  );
}
